import useAuthStore from '@/stores/AuthStore'
import useSnippetStore from '@/stores/SnippetStore'
import { useOrderStore } from '@/stores/OrderStore'
import useUrlStore from '@/stores/UrlStore'
import useConversationStore from '@/stores/ConversationStore'
import { useThemeContext } from '@/stores/ThemeContext'
import { DownOutlined } from '@ant-design/icons'
import {
  Avatar,
  Col,
  Dropdown,
  Layout,
  Menu,
  Row,
  Space,
  Typography,
  theme,
  Badge,
  Drawer,
} from 'antd'
import 'dayjs/locale/zh-cn'
import { useEffect, useState, useCallback, useRef } from 'react'
import { Link, NavLink, Outlet, useHref } from 'react-router-dom'

import '@/assets/App.css'
import AppLogo from '@/assets/highlights_travel_300_300_250613.png'
import 'react-chat-elements/dist/main.css'
import ReloadPrompt from './ReloadPrompt'
import ClearCache from './ClearCache'

import { BUILD_VERSION, GIT_HEAD } from '@/config'

const { Header, Footer, Content } = Layout
const { Title } = Typography

function DesktopApp() {
  const { colorPrimary } = useThemeContext()
  const loginUser = useAuthStore(s => s.loginUser)

  const allowedUserIds = ['383', '227', '404', '178', '343', '114', '354', '691', '550', '370', '213', '341', '422', '496', '495', '611'];
  const canUseCustomerProfile = loginUser.userId && allowedUserIds.includes(loginUser.userId)

  const href = useHref()

  const totalNotify = useConversationStore(s => s.totalNotify)

  const [
    openSnippetDrawer,
    closeSnippetDrawer,
    snippetDrawerOpen,
  ] = useSnippetStore(s => [
    s.openDrawer,
    s.closeDrawer,
    s.drawerOpen,
  ])

  const [
    openPaymentDrawer,
    closePaymentDrawer,
    paymentDrawerOpen,
  ] = useOrderStore(s => [
    s.openDrawer,
    s.closeDrawer,
    s.drawerOpen,
  ])
  const [
    openShorturlDrawer,
    closeShorturlDrawer,
    shorturlDrawerOpen,
  ] = useUrlStore(s => [
    s.openDrawer,
    s.closeDrawer,
    s.drawerOpen,
  ])

  const onClick = ({ key }) => {
    if (key === 'snippet-list') {
      openSnippetDrawer()
    } else if (key == 'generate-payment') {
      openPaymentDrawer()
    } else if (key === 'shorturl-conversion') {
      openShorturlDrawer()
    }
  }

  let defaultPath = '/order/follow'

  if (href !== '/') {
    const splitPath = href.split('/')
    if (splitPath.length > 2) {
      defaultPath = '/' + splitPath[1] + '/' + splitPath[2]
    }
  }

  const {
    token: { colorBgContainer },
  } = theme.useToken()

  /**
   * 标签页标题闪烁
   */
  const [isTitleVisible, setIsTitleVisible] = useState(true)
  const step = useCallback(() => {
    document.title = isTitleVisible
      ? `🔔🔥💬【${totalNotify}条新消息】`
      : '______________';
    setIsTitleVisible(!isTitleVisible);
  }, [isTitleVisible, totalNotify]);

  useEffect(() => {
    let intervalId;
    if (totalNotify > 0) {
      if ('setAppBadge' in navigator) {
        navigator.setAppBadge(totalNotify).catch(() => {});
      }
      intervalId = setInterval(step, 1000); // 1000ms = 1s
    } else {
      document.title = '销售平台';
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {})
      }
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [totalNotify, isTitleVisible]);

  return (
    <Layout>
      <Header
        className='header'
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          width: '100%',
          background: 'white',
        }}>
        <Row gutter={{ md: 24 }} align='middle'>
          <Col flex='240px'>
            <NavLink to='/'>
              <img src={AppLogo} className='logo' alt='App logo' />
            </NavLink>
            <Title level={3}>销售平台</Title>
          </Col>
          <Col span={10}>
            <Menu
              mode='horizontal'
              selectedKeys={[defaultPath]}
              items={[
                {
                  key: '/order/follow',
                  label: <Link to='/order/follow'>订单跟踪</Link>,
                },
                {
                  key: '/order/chat',
                  label: (
                    <Link to='/order/chat'>
                      在线聊天
                      <Badge
                        count={totalNotify > 0 ? totalNotify : undefined}
                        style={{
                          backgroundColor: '#52c41a',
                        }}
                      />
                    </Link>
                  ),
                },
                // {
                //   key: '/callcenter/call',
                //   label: <Link to='/callcenter/call'>语音通话</Link>,
                // },
                {
                  key: '/chat/history',
                  label: <Link to='/chat/history'>历史记录</Link>,
                },
                {
                  key: '/customer_relation/index',
                  label: <Link to='/customer_relation/index'>客户运营</Link>,
                },
                canUseCustomerProfile ? { key: '/customer/profile', label: <Link to='/customer/profile'>客户画像</Link> } : null,
              ]}
            />
          </Col>
          <Col
            flex='auto'
            style={{
              color: 'white',
              marginBottom: '0',
              display: 'flex',
              justifyContent: 'end',
            }}>
            <ReloadPrompt style={{marginRight: 'auto'}} />
            <Dropdown
              menu={{
                items: [
                  {
                    label: <Link to='/account/profile'>个人资料</Link>,
                    key: 'profile',
                  },
                  { type: 'divider' },
                  {
                    label: '支付链接',
                    key: 'generate-payment',
                  },
                  {
                    label: '短链接转换',
                    key: 'shorturl-conversion',
                  },
                  {
                    label: '图文集',
                    key: 'snippet-list',
                  },
                  { type: 'divider' },
                  { label: <ReloadPrompt force />, key: 'reload' },
                  { type: 'divider' },
                  { label: <ClearCache />, key: 'clearcache' },
                  { type: 'divider' },
                  {
                    label: <Link to='/p/dingding/logout'>退出</Link>,
                    key: 'logout',
                  },
                ],
                onClick,
              }}
              trigger={['click']}>
              <a
                onClick={(e) => e.preventDefault()}
                style={{ color: colorPrimary }}>
                <Space>
                  <Avatar src={loginUser.avatarUrl}>
                    {loginUser?.username?.substring(1)}
                  </Avatar>
                  {loginUser.username}
                  <DownOutlined />
                </Space>
              </a>
            </Dropdown>
          </Col>
        </Row>
      </Header>
      <Layout>
        <Content
          style={{
            padding: 8,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
          }}>
          <Outlet />
        </Content>
      </Layout>
      <Footer>
        桂林海纳国际旅行社有限公司 Version: {BUILD_VERSION}({GIT_HEAD})
      </Footer>
    </Layout>
  )
}

export default DesktopApp
